import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

interface UseUniqueValidationOptions {
  table: "tables" | "commands" | "products" | "categories";
  column: string;
  excludeId?: string; // For edit mode - exclude current record
  debounceMs?: number;
}

interface ValidationResult {
  isChecking: boolean;
  isUnique: boolean | null;
  error: string | null;
}

export function useUniqueValidation({
  table,
  column,
  excludeId,
  debounceMs = 300,
}: UseUniqueValidationOptions) {
  const { activeRestaurant } = useRestaurantContext();
  const [result, setResult] = useState<ValidationResult>({
    isChecking: false,
    isUnique: null,
    error: null,
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastValueRef = useRef<string>("");

  const checkUnique = useCallback(
    async (value: string) => {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const trimmedValue = value.trim();
      
      // If value is empty, reset state
      if (!trimmedValue) {
        setResult({ isChecking: false, isUnique: null, error: null });
        lastValueRef.current = "";
        return;
      }

      // If same value, don't recheck
      if (trimmedValue === lastValueRef.current) {
        return;
      }

      // Set checking state immediately
      setResult((prev) => ({ ...prev, isChecking: true, error: null }));

      // Debounce the actual check
      timeoutRef.current = setTimeout(async () => {
        if (!activeRestaurant) {
          setResult({ isChecking: false, isUnique: null, error: "Restaurante não selecionado" });
          return;
        }

        try {
          // Build the query dynamically to avoid TypeScript recursion issues
          const { data, error } = await (supabase
            .from(table as any)
            .select("id")
            .eq("restaurant_id", activeRestaurant.id)
            .eq(column, trimmedValue) as any)
            .neq("id", excludeId || "00000000-0000-0000-0000-000000000000")
            .limit(1);

          if (error) {
            console.error("Error checking uniqueness:", error);
            setResult({ isChecking: false, isUnique: null, error: "Erro ao verificar" });
            return;
          }

          const isUnique = !data || data.length === 0;
          lastValueRef.current = trimmedValue;
          setResult({ isChecking: false, isUnique, error: null });
        } catch (error) {
          console.error("Error checking uniqueness:", error);
          setResult({ isChecking: false, isUnique: null, error: "Erro ao verificar" });
        }
      }, debounceMs);
    },
    [activeRestaurant, table, column, excludeId, debounceMs]
  );

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    lastValueRef.current = "";
    setResult({ isChecking: false, isUnique: null, error: null });
  }, []);

  return {
    ...result,
    checkUnique,
    reset,
  };
}
